import { useState, useRef, useEffect } from 'react';
import { Select, SearchableSelect, InputContainer, Typography, Button } from '../../design-system';
import { ContainersView } from './ContainersView';
import {
  DashboardWrapper,
  DashboardPopup,
  DashboardHeader,
  DashboardInputs,
  FilterIconButton,
  CloseIconButton,
  DashboardTable,
  DashboardTableHeader,
  DashboardTableRows,
  DashboardTableRow,
  DashboardTableCell,
  DashboardTableRowLoading,
} from '../dashboard';
import { useCallBackOnBack, useContainersQuery, useIsVisible } from '../../hooks';
import { containersService } from '../../services';
import { useStore } from '../../context';
import { containerTypes, containerImageTypes, containerStatues } from '../../const';
import { formatContainerType, showToRoles } from '../../utils';

export const Containers = () => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const [isOpenFormPopup, setIsOpenFormPopup] = useState(false);
  const [isVisibleLoading, loadingRef] = useIsVisible();

  const [containerNumber, setContainerNumber] = useState('');
  const [containerType, setContainerType] = useState('');
  const [containerStatus, setContainerStatus] = useState('GATED_IN');
  const [containerImageType, setContainerImageType] = useState('all');
  const [clientCode, setClientCode] = useState('');
  const [filters, setFilters] = useState<any>({
    containerType: '',
    containerStatus: 'GATED_IN',
    containerImageType: 'all',
    clientCode: '',
  });
  const selectedContainer = useRef({});
  const isFiltersChange =
    filters.containerType !== containerType ||
    filters.containerStatus !== containerStatus ||
    filters.containerImageType !== containerImageType ||
    filters.clientCode !== clientCode;

  const {
    data: { data, totalCount },
    isSuccess,
    refetch,
    isFetched,
    fetchNextPage,
    isFetching,
  } = useContainersQuery({
    number: containerNumber,
    status: filters.containerStatus,
    type: filters.containerType,
    imageType: filters.containerImageType,
    clientCode: filters.clientCode,
    containerImageType: filters.containerImageType,
  });

  useEffect(() => {
    if (!isVisibleLoading || isFetching) return;

    fetchNextPage();
  }, [isVisibleLoading]);

  const handleToggleOpenPopup = () => {
    if (isOpenPopup) {
      setContainerType(filters.containerType);
      setContainerStatus(filters.containerStatus);
      setContainerImageType(filters.containerImageType);
      setClientCode(filters.clientCode);
    }

    setIsOpenPopup(!isOpenPopup);
  };

  useCallBackOnBack(handleToggleOpenPopup, isOpenPopup);

  const handleClientCodeSelect = (option: any) => {
    setClientCode(option.label);
  };

  const handleRowClick = (option: any) => {
    selectedContainer.current = option;
    setIsOpenFormPopup(true);
  };

  const handleApplyFiltersClick = () => {
    const newFilters: any = {
      containerType,
      containerStatus,
      containerImageType,
      clientCode,
    };

    setFilters(newFilters);
    setIsOpenPopup(!isOpenPopup);
  };

  const handleFormCloseClick = () => {
    setIsOpenFormPopup(false);
  };

  return (
    <DashboardWrapper>
      <DashboardPopup isOpen={isOpenPopup}>
        <DashboardHeader>
          <Typography variant={'textXL'} weight={'bold'}>
            Filters
          </Typography>
          <CloseIconButton onClick={handleToggleOpenPopup} />
        </DashboardHeader>
        <DashboardInputs>
          <Select
            label={'Image Filter'}
            placeholder={'Select Image Filter'}
            options={containerImageTypes}
            value={containerImageType}
            onSelect={setContainerImageType}
          />
          <Select
            label={'Container Type'}
            placeholder={'Select Container Type'}
            options={containerTypes}
            value={containerType}
            onSelect={setContainerType}
            withClearButton
          />
          <Select
            label={'Container Status'}
            placeholder={'Select Container Status'}
            options={containerStatues}
            value={containerStatus}
            onSelect={setContainerStatus}
          />
          <SearchableSelect
            label={'Client Code'}
            selectedOptionLabel={clientCode || 'Select Client Code'}
            onSelect={handleClientCodeSelect}
            onClear={handleClientCodeSelect}
            queryKey={'client-code-options'}
            queryFn={() => {
              if (showToRoles('admin', user))
                return containersService.clientCodeOptions(!!showToRoles(['SUPER_ADMIN'], user));

              return () => [];
            }}
            selectedAreasEqaul={{ key: 'label', keys: [clientCode] }}
            withClearButton={!!clientCode}
          />
          <Button size={'medium'} onClick={handleApplyFiltersClick} disabled={!isFiltersChange}>
            Apply Filters
          </Button>
        </DashboardInputs>
      </DashboardPopup>
      <ContainersView
        container={selectedContainer.current}
        isOpen={isOpenFormPopup}
        refetch={refetch}
        handleFormCloseClick={handleFormCloseClick}
      />
      <DashboardHeader>
        <InputContainer
          placeholder={'Container Number'}
          data-size={'medium'}
          defaultValue={containerNumber}
          onChange={(e) => setContainerNumber(e.target.value)}
        />
        <FilterIconButton onClick={handleToggleOpenPopup} />
      </DashboardHeader>
      <DashboardTable>
        <DashboardTableHeader>
          <DashboardTableCell>Container</DashboardTableCell>
          <DashboardTableCell>Type</DashboardTableCell>
          <DashboardTableCell align={'right'}>Code</DashboardTableCell>
        </DashboardTableHeader>
        <DashboardTableRows>
          {isSuccess &&
            data.map((row: any, i: any) => (
              <DashboardTableRow key={i} onClick={() => handleRowClick(row)}>
                <DashboardTableCell>{row.number}</DashboardTableCell>
                <DashboardTableCell>{formatContainerType(row.type)}</DashboardTableCell>
                <DashboardTableCell align={'right'}>{row.clientCode}</DashboardTableCell>
              </DashboardTableRow>
            ))}
          {(!isFetched || data.length !== totalCount) && (
            <DashboardTableRowLoading key={data.length} setRef={loadingRef} />
          )}
        </DashboardTableRows>
      </DashboardTable>
    </DashboardWrapper>
  );
};
